import { Autocomplete, Box, Chip, CircularProgress, TextField } from "@mui/material";
import { Fragment } from "react";
import { useSupabaseLoader } from "../../../../../../server/supabase/hooks";
import { notEmpty } from "../../../../../../utils/not-empty";
import { SupabasePersonService } from "../../../../../employees/server/supabase-person-service";

import type { UsableAccountPersonField } from "../../../../server/supabase-segment-field-service";
import type { Condition } from "../../../../types/segment-schema";
import type { SxProps } from "@mui/material";
import type { FC } from "react";

export const AutocompleteCountrySelect: FC<{
  condition: Condition;
  segmentField?: UsableAccountPersonField;
  handleFieldChange: (value: string[]) => void;
  errMsg?: string;
  disabled?: boolean;
  hidden?: boolean;
  sx?: SxProps;
}> = ({ condition, handleFieldChange, errMsg: error, disabled, hidden, segmentField, sx }) => {
  const { loading, data } = useSupabaseLoader(
    async ({ supabase, account_id }) => {
      if (!disabled && segmentField) {
        const { data: uniqueFieldsData } = await new SupabasePersonService(
          supabase,
        ).getUniqueValuesForCountryType(account_id, segmentField.key ?? "");

        if (uniqueFieldsData) {
          return {
            options: uniqueFieldsData
              .filter((field) => Boolean(field.value) && field.key === segmentField.key)
              .map((field) => field.value)
              .filter(notEmpty)
              .sort(),
            countryInfo: uniqueFieldsData.reduce<Record<string, string>>((prev, val) => {
              if (!val) return prev;
              if (!val.value || !val.name) return prev;

              return { ...prev, [val.value]: val.name };
            }, {}),
          };
        }
      }

      return { options: [], countryInfo: {} };
    },
    [disabled, JSON.stringify(segmentField)],
  );

  return (
    <Autocomplete
      multiple
      size="small"
      options={data?.options ?? []}
      loading={loading}
      hidden={hidden}
      disabled={disabled || loading || (data?.options ?? []).length === 0}
      sx={sx}
      value={condition.values ? condition.values : []}
      onChange={(_, value) => handleFieldChange(value ? value.slice() : [])}
      getOptionLabel={(option) => data?.countryInfo[option] ?? "Unknown"}
      renderOption={(props, option) => (
        <Box component="li" {...props} key={option}>
          <Box sx={{ mr: 1, flexShrink: 0 }}>
            <img
              width="20"
              height="11"
              src={`https://flagcdn.com/w20/${option.toLowerCase()}.png`}
              alt=""
            />
          </Box>
          {data?.countryInfo[option] ?? "Unknown"}
        </Box>
      )}
      renderValue={(tagValue, getTagProps, state) =>
        tagValue.map((option, index) => (
          <Chip {...getTagProps({ index })} key={option} label={state.getOptionLabel(option)} />
        ))
      }
      renderInput={(params) => (
        <Box sx={{ mt: 0.4 }}>
          <TextField
            {...params}
            sx={{ "& label": { mt: -1.4 } }}
            error={Boolean(error)}
            helperText={error}
            label={segmentField?.display_name}
            variant="standard"
            slotProps={{
              input: {
                ...params.InputProps,
                endAdornment: (
                  <Fragment>
                    {loading ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </Fragment>
                ),
              },
            }}
          />
        </Box>
      )}
    />
  );
};
